import { useEffect, useState } from "react";
import Modal from "react-modal";
import * as XLSX from "xlsx";
import { FooterComponents } from "../FooterComponent";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import { GraduationCap, Search, Calculator, CheckCircle, XCircle, Award, Send, AlertCircle, History, User, Calendar, Mail, ShieldCheck } from "lucide-react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getToken, getUserRole } from '../../utils/auth';

export default function EducationProgramApplicantList() {
  // Modal state for documents
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDocuments, setModalDocuments] = useState([]);
  const [modalStudent, setModalStudent] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState(null);
  // Tracks the document/action currently being fetched, e.g. "preview-<id>".
  const [docActionLoading, setDocActionLoading] = useState(null);

  // Bulk update modal state
  const [isBulkUpdateModalOpen, setIsBulkUpdateModalOpen] = useState(false);
  const [bulkUpdateStatus, setBulkUpdateStatus] = useState("");
  const [bulkFilterStatus, setBulkFilterStatus] = useState("ALL");
  const [bulkSendEmail, setBulkSendEmail] = useState(true);
  const [bulkConfirmed, setBulkConfirmed] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkUpdateProgress, setBulkUpdateProgress] = useState({ current: 0, total: 0 });
  const [bulkUpdateResults, setBulkUpdateResults] = useState({ success: 0, failed: 0, skipped: 0 });

  // Audit modal state
  const [auditApplication, setAuditApplication] = useState(null);

  //method to update application status
  const updateApplicationStatus = async (applicationId, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [applicationId]: true }));
    try {
      await axios.put(
        process.env.REACT_APP_BASE_URL +
          `hec/abroad-scholarship-application/update?applicationId=${applicationId}&updatedApplication=${newStatus}`,
        null,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      // Update the local state to reflect the change immediately
      setStudentList(prevList => 
        prevList.map(student => 
          student.id === applicationId 
            ? { ...student, applicationStatus: newStatus }
            : student
        )
      );
    } catch (err) {
      alert("Failed to update application status. Please try again.");
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [applicationId]: false }));
    }
  };



  // Fetch documents for a student
  const handleOpenDocuments = async (application) => {
    setIsModalOpen(true);
    setModalDocuments([]);
    setDocLoading(true);
    setDocError(null);
    setModalStudent(application.id);
    try {
      const response = await axios.get(
        process.env.REACT_APP_BASE_URL +
          `hec/applicants-document/documents?applicationId=${application.id}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setModalDocuments(response.data || []);
    } catch (err) {
      setDocError("Failed to fetch documents: " + err.message);
    } finally {
      setDocLoading(false);
    }
    if (application.applicationStatus === "RECEIVED") {
      updateApplicationStatus(application.id, "UNDER_REVIEW");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalDocuments([]);
    setModalStudent(null);
    setDocError(null);
  };

  // The download-document endpoint requires a JWT, so it cannot be reached by a
  // plain browser navigation (window.open / <a href>) — those omit the
  // Authorization header and the server responds 403. Instead we fetch the file
  // with the token as a blob and serve it from a local object URL.
  const fetchDocumentBlob = async (docId, mode) => {
    const response = await axios.get(
      process.env.REACT_APP_BASE_URL +
        `hec/abroad-scholarship-application/download-document?documentPath=${docId}&mode=${mode}`,
      {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: "blob",
      }
    );
    return response.data;
  };

  const handlePreviewDocument = async (docId) => {
    // Open the tab synchronously (within the click handler) so the browser does
    // not treat it as a blocked popup once the async fetch resolves.
    const previewWindow = window.open("", "_blank");
    setDocActionLoading(`preview-${docId}`);
    try {
      const blob = await fetchDocumentBlob(docId, "preview");
      const url = window.URL.createObjectURL(blob);
      if (previewWindow) {
        previewWindow.location = url;
      } else {
        // Popup was blocked; fall back to the current tab.
        window.open(url, "_blank");
      }
      // Revoke after a delay so the opened tab has time to load the blob.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (previewWindow) previewWindow.close();
      toast.error("Failed to preview document. Please try again.");
    } finally {
      setDocActionLoading(null);
    }
  };

  const handleDownloadDocument = async (docId, fileName) => {
    setDocActionLoading(`download-${docId}`);
    try {
      const blob = await fetchDocumentBlob(docId, "download");
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download document. Please try again.");
    } finally {
      setDocActionLoading(null);
    }
  };
  const navigate = useNavigate();
  const { id } = useParams(); // get program id from URL params
  const [studentList, setStudentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [programsPerPage] = useState(50); // Set items per page
  const [updatingStatus, setUpdatingStatus] = useState({}); // Track updating status for each application
  const [tempStatus, setTempStatus] = useState({}); // Track temporary status selections
  const [evaluationScores, setEvaluationScores] = useState([]); // Store evaluation scores
  const [isCalculating, setIsCalculating] = useState(false); // Track calculation progress
  const [calculationProgress, setCalculationProgress] = useState({ current: 0, total: 0 });

  // Get user role from JWT token
  const userRole = getUserRole();

  // Handle temporary status change (just update dropdown, don't save yet)
  const handleStatusChange = (applicationId, newStatus) => {
    setTempStatus(prev => ({ ...prev, [applicationId]: newStatus }));
  };

  // Handle actual status update when button is pressed
  const handleStatusUpdate = async (applicationId) => {
    const newStatus = tempStatus[applicationId];
    if (!newStatus) return;
    
    await updateApplicationStatus(applicationId, newStatus);
    // Clear temp status after successful update
    setTempStatus(prev => {
      const newTemp = { ...prev };
      delete newTemp[applicationId];
      return newTemp;
    });
  };

  // Fetch evaluation scores for the program
  const fetchEvaluationScores = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}hec/applicationEvaluationScore/by-program?programId=${id}`,
        {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      setEvaluationScores(response.data || []);
    } catch (err) {
      console.error("Failed to fetch evaluation scores:", err);
    }
  };

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_BASE_URL +
            `hec/abroad-scholarship-application/get-by-program?programId=${id}`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setStudentList(response.data);
        console.log("Fetched applications:", response.data);
        // Fetch evaluation scores after getting applications
        await fetchEvaluationScores();
      } catch (err) {
        //setError("Failed to fetch education programs: " + err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, [id]); // Add id as a dependency

  const filteredPrograms = studentList.filter(
    (application) =>
      application.user?.firstName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      application.user?.lastName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      application.user?.email
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      application.applyLevel
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      application.academicMerit?.letterGrade
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      application.priorityCourse?.courseName
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
        application.priorityCourse?.courseSector
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      application.workingPlace?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate programs for the current page
  const indexOfLastProgram = currentPage * programsPerPage;
  const indexOfFirstProgram = indexOfLastProgram - programsPerPage;
  const currentPrograms = filteredPrograms.slice(
    indexOfFirstProgram,
    indexOfLastProgram
  );

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredPrograms.length / programsPerPage);

  const renderPaginationButtons = () => {
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <button
          key={i}
          onClick={() => paginate(i)}
          className={`mx-1 px-3 py-1 rounded-lg text-sm font-medium ${
            currentPage === i
              ? "bg-hec-700 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {i}
        </button>
      );
    }
    return pageNumbers;
  };


  const getApplicationStatus = (status) => {
    switch (status) {
      case "RECEIVED":
        return "RECEIVED BY HEC";
      case "UNDER_REVIEW":
        return "ASSIGNED FOR REVIEW";
      case "SUBMITTED_TO_PARTENER":
        return "RECEIVED BY PARTNER";
      case "SUBMITTED_TO_UNIVERSITY":
        return "RECEIVED BY UNIVERSITY";
      case "GRANTED":
        return "GRANTED";
      case "REJECTED":
        return "NOT GRANTED";
      case "APPROVED":
        return "APPROVED";
      case "PENDING":
        return "PENDING";
      default:
        return "PENDING";
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium";
    switch (status) {
      case "RECEIVED":
      case "RECEIVED BY HEC":
        return `${baseClasses} bg-blue-100 text-blue-800`;
      case "UNDER_REVIEW":
      case "UNDER REVIEW":
      case "ASSIGNED FOR REVIEW":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case "SUBMITTED_TO_PARTENER":
      case "RECEIVED BY PARTNER":
        return `${baseClasses} bg-purple-100 text-purple-800`;
      case "SUBMITTED_TO_UNIVERSITY":
      case "RECEIVED BY UNIVERSITY":
        return `${baseClasses} bg-indigo-100 text-indigo-800`;
      case "GRANTED":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "REJECTED":
      case "NOT GRANTED":
        return `${baseClasses} bg-red-100 text-red-800`;
      case "APPROVED":
        return `${baseClasses} bg-green-100 text-green-800`;
      case "PENDING":
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };


  // Calculate scores for all applications
  const handleCalculateScores = async () => {
    if (isCalculating) return;
    
    const confirmed = window.confirm(
      `This will calculate evaluation scores for all ${studentList.length} applications in this program. Continue?`
    );
    
    if (!confirmed) return;

    setIsCalculating(true);
    setCalculationProgress({ current: 0, total: studentList.length });
    
    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < studentList.length; i++) {
        const application = studentList[i];
        setCalculationProgress({ current: i + 1, total: studentList.length });
        
        try {
          await axios.get(
            `${process.env.REACT_APP_BASE_URL}hec/applicationEvaluationScore/calculate?applicationId=${application.id}&programId=${id}`,
            {
              headers: {
                Authorization: `Bearer ${getToken()}`,
              },
            }
          );
          successCount++;
        } catch (err) {
          console.error(`Failed to calculate score for application ${application.id}:`, err);
          errorCount++;
        }
      }

      // Refresh evaluation scores after calculation
      await fetchEvaluationScores();

      toast.success(
        `Score calculation complete! Success: ${successCount}, Failed: ${errorCount}`,
        {
          position: "top-center",
          autoClose: 5000,
          theme: "colored",
        }
      );
    } catch (err) {
      toast.error("An error occurred during score calculation", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsCalculating(false);
      setCalculationProgress({ current: 0, total: 0 });
    }
  };

  // Get graded applications count
  const getGradedCount = () => {
    return evaluationScores.length;
  };

  // Get not graded applications count
  const getNotGradedCount = () => {
    return studentList.length - evaluationScores.length;
  };

  // Calculate affected applications for bulk update
  const getAffectedApplicationsCount = () => {
    return studentList.filter(app => {
      // Skip GRANTED applications
      if (app.applicationStatus === "GRANTED") return false;
      
      // If filter is set, only include matching status
      if (bulkFilterStatus !== "ALL" && app.applicationStatus !== bulkFilterStatus) {
        return false;
      }
      
      return true;
    }).length;
  };

  // Handle bulk update modal open
  const handleOpenBulkUpdateModal = () => {
    setBulkUpdateStatus("");
    setBulkFilterStatus("ALL");
    setBulkSendEmail(true);
    setBulkConfirmed(false);
    setBulkUpdateResults({ success: 0, failed: 0, skipped: 0 });
    setIsBulkUpdateModalOpen(true);
  };

  // Handle bulk update modal close
  const handleCloseBulkUpdateModal = () => {
    if (isBulkUpdating) {
      const confirmed = window.confirm("Bulk update is in progress. Are you sure you want to cancel?");
      if (!confirmed) return;
    }
    setIsBulkUpdateModalOpen(false);
    setBulkUpdateStatus("");
    setBulkFilterStatus("ALL");
    setBulkConfirmed(false);
    setIsBulkUpdating(false);
    setBulkUpdateProgress({ current: 0, total: 0 });
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async () => {
    if (!bulkUpdateStatus || !bulkConfirmed) {
      toast.error("Please select a status and confirm the update", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
      return;
    }

    const applicationsToUpdate = studentList.filter(app => {
      // Skip GRANTED applications
      if (app.applicationStatus === "GRANTED") return false;
      
      // If filter is set, only include matching status
      if (bulkFilterStatus !== "ALL" && app.applicationStatus !== bulkFilterStatus) {
        return false;
      }
      
      return true;
    });

    if (applicationsToUpdate.length === 0) {
      toast.warning("No applications to update with the selected criteria", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
      return;
    }

    const confirmMessage = `This will update ${applicationsToUpdate.length} application(s) to status "${getApplicationStatus(bulkUpdateStatus)}". ${bulkSendEmail ? 'Email notifications will be sent.' : 'No emails will be sent.'}\n\nContinue?`;
    
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setIsBulkUpdating(true);
    setBulkUpdateProgress({ current: 0, total: applicationsToUpdate.length });
    
    let successCount = 0;
    let errorCount = 0;
    let skippedCount = 0;

    const updatedApplications = [];

    try {
      for (let i = 0; i < applicationsToUpdate.length; i++) {
        const application = applicationsToUpdate[i];
        setBulkUpdateProgress({ current: i + 1, total: applicationsToUpdate.length });
        
        try {
          // Check again if not GRANTED (safety check)
          if (application.applicationStatus === "GRANTED") {
            skippedCount++;
            continue;
          }

          await axios.put(
            process.env.REACT_APP_BASE_URL +
              `hec/abroad-scholarship-application/update?applicationId=${application.id}&updatedApplication=${bulkUpdateStatus}`,
            null,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          
          successCount++;
          updatedApplications.push(application.id);
        } catch (err) {
          console.error(`Failed to update application ${application.id}:`, err);
          errorCount++;
        }
      }

      // Update local state for all successfully updated applications
      setStudentList(prevList => 
        prevList.map(student => 
          updatedApplications.includes(student.id)
            ? { ...student, applicationStatus: bulkUpdateStatus }
            : student
        )
      );

      setBulkUpdateResults({ success: successCount, failed: errorCount, skipped: skippedCount });

      // Show summary toast
      if (errorCount === 0 && skippedCount === 0) {
        toast.success(
          `Bulk update complete! Successfully updated ${successCount} application(s).`,
          {
            position: "top-center",
            autoClose: 5000,
            theme: "colored",
          }
        );
      } else {
        toast.warning(
          `Bulk update complete! Success: ${successCount}, Failed: ${errorCount}, Skipped: ${skippedCount}`,
          {
            position: "top-center",
            autoClose: 7000,
            theme: "colored",
          }
        );
      }

      // Keep modal open to show results
      setBulkConfirmed(false);
      
    } catch (err) {
      toast.error("An error occurred during bulk update", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setIsBulkUpdating(false);
      setBulkUpdateProgress({ current: 0, total: 0 });
    }
  };

   // Download Excel for all table data
  const handleDownloadExcel = () => {
    // Prepare data for Excel
    const excelData = filteredPrograms.map((application) => ({
      "First Name": application.user?.firstName || "",
      "Last Name": application.user?.lastName || "",
      "Email": application.user?.email || "",
      "National ID": application.user?.nationalId || "",
      "Phone Number": application.user?.phoneNumber || "",
      "Gender": application.user?.gender || "",
      "Current Level": application?.currentLevel || "",
      "Apply Level": application?.applyLevel || "",
      "Working Place": application?.workingPlace || "",
      "Field of Study": application?.priorityCourse?.courseSector || "",
      "Priority Course": application?.priorityCourse?.courseName || "",
      "Priority Level": application?.priorityCourse?.priorityLevel || "",
      "Country of Study": application?.countryOfStudy || "",
      "University Applied For": application?.universityAppliedFor || "",
      "Course Applied For": application?.courseAppliedFor || "",
      "Previous Study Completion Year": application?.previousStudyCompletionYear || "",
      "Academic Performance Percentage": application?.academicPerformancePercentage || "",
      "Start Date": application?.startDate ? formatDate(application.startDate) : "",
      "End Date": application?.endDate ? formatDate(application.endDate) : "",
      "Tuition Fee": application?.tuitionFee ? `$${application.tuitionFee?.toLocaleString()}` : "",
      "Monthly Living Allowance": application?.monthlyLivingAllowance ? `$${application.monthlyLivingAllowance?.toLocaleString()}` : "",
      "Research Allowance": application?.researchAllowance ? `$${application.researchAllowance?.toLocaleString()}` : "",
      "Air Ticket Allowance": application?.airTicketAllowance ? `$${application.airTicketAllowance?.toLocaleString()}` : "",  
      "Academic Merit": application?.academicMerit?.letterGrade || "",
      "Application Status": application?.applicationStatus || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Applicants");
    XLSX.writeFile(workbook, "scholarship_applicants.xlsx");
  };


  // Status options for dropdown
  const statusOptions = [
    { value: "RECEIVED", label: "RECEIVED BY HEC" },
    { value: "UNDER_REVIEW", label: "UNDER REVIEW" },
    { value: "SUBMITTED_TO_PARTENER", label: "RECEIVED BY PARTNER" },
    { value: "SUBMITTED_TO_UNIVERSITY", label: "RECEIVED BY UNIVERSITY" },
    { value: "GRANTED", label: "GRANTED" },
    { value: "REJECTED", label: "REJECTED" }
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="pt-6 sm:ml-64">
        <div className="p-6 min-h-screen">
          <div className="py-8 mt-10">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-hec-700 mb-2">
                Education Programs
              </h1>
              <p className="text-hec-600">
                List of available education programs for the selected
                scholarship.
              </p>
            </div>

            {/* Search Bar */}
            <div className="bg-white rounded-lg shadow-sm border border-hec-200 p-6 mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-hec-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search programs by name or university..."
                  className="w-full pl-10 pr-4 py-3 border border-hec-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-transparent transition-all duration-200 text-hec-900"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            {/* Loading, Error, or Table */}
            {loading ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-spin">
                  <GraduationCap className="w-8 h-8 text-hec-400" />
                </div>
                <p className="text-hec-600">Loading programs...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12 bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
                <p className="font-medium">Error: {error}</p>
                <p>Please try again later.</p>
              </div>
            ) : (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-hec-200 overflow-visible">
                 {/* Statistics and Action Buttons */}
                  <div className="flex justify-between items-center m-4">
                    {/* Statistics Display */}
                    <div className="flex items-center space-x-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-5 h-5 text-blue-600" />
                          <div>
                            <p className="text-xs text-blue-600 font-medium">Graded Applications</p>
                            <p className="text-lg font-bold text-blue-700">{getGradedCount()}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2">
                        <div className="flex items-center space-x-2">
                          <XCircle className="w-5 h-5 text-orange-600" />
                          <div>
                            <p className="text-xs text-orange-600 font-medium">Not Graded</p>
                            <p className="text-lg font-bold text-orange-700">{getNotGradedCount()}</p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Total Applications</p>
                          <p className="text-lg font-bold text-gray-700">{studentList.length}</p>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                       <button
                        onClick={handleCalculateScores}
                        disabled={isCalculating || studentList.length === 0}
                        className="flex items-center space-x-2 bg-hec-600 hover:bg-hec-700 text-white px-4 py-2 rounded-lg font-medium shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-hec-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Calculator className="w-5 h-5" />
                        <span>{isCalculating ? `Calculating... (${calculationProgress.current}/${calculationProgress.total})` : 'Calculate Scores'}</span>
                      </button>
                      <button
                        onClick={handleOpenBulkUpdateModal}
                        disabled={studentList.length === 0}
                        className="flex items-center space-x-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-5 h-5" />
                        <span>Bulk Update Status</span>
                      </button>
                      <button
                        onClick={() => navigate(`/evaluation-scores/${id}`)}
                        disabled={getGradedCount() === 0}
                        className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Award className="w-5 h-5" />
                        <span>View Scores</span>
                      </button>
                      <button
                        onClick={handleDownloadExcel}
                        className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium shadow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
                      >
                        <span>Download Excel</span>
                      </button>
                    </div>
                  </div>
                <div className="overflow-x-auto">
                 
                  <table className="min-w-[3200px] w-full">
                    <thead className="bg-hec-850 border-b border-hec-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          First Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Last Name
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          National ID
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Phone Number
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Gender
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Current Level
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Apply Level
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Working Place
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          FIELD OF STUDY
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Priority Course
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Priority Level
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Country
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          University
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Course
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Completion Year
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Performance %
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Start Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          End Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Tuition Fee
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Academic Merit
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                          Application Status
                        </th>
                        <th className="px-4 py-3 text-xs text-center font-medium text-white uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-hec-100">
                      {currentPrograms.map((application) => (
                        <tr
                          key={application.id}
                          className="hover:bg-hec-50 transition-colors duration-150"
                        >
                          <td className="px-4 py-3 text-hec-900">
                            {application.user?.firstName || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.user?.lastName || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.user?.email || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.user?.nationalId || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.user?.phoneNumber || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.user?.gender || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.currentLevel || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.applyLevel || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.workingPlace || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.priorityCourse?.courseSector || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.priorityCourse?.courseName || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                application.priorityCourse?.priorityLevel ===
                                "HIGH"
                                  ? "bg-red-100 text-red-800"
                                  : application.priorityCourse
                                      ?.priorityLevel === "MEDIUM"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : application.priorityCourse
                                      ?.priorityLevel === "LOW"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                              {application.priorityCourse?.priorityLevel ||
                                "N/A"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.countryOfStudy || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.universityAppliedFor || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.courseAppliedFor || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.previousStudyCompletionYear || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.academicPerformancePercentage || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {formatDate(application.startDate)}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {formatDate(application.endDate)}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.tuitionFee
                              ? `$${application.tuitionFee?.toLocaleString()}`
                              : "N/A"}
                          </td>
                          <td className="px-4 py-3 text-hec-900">
                            {application.academicMerit?.letterGrade || "N/A"}
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${
                                application.applicationStatus === "RECEIVED"
                                  ? "bg-blue-100 text-blue-800"
                                  : application.applicationStatus === "APPROVED"
                                  ? "bg-green-100 text-green-800"
                                  : application.applicationStatus === "REJECTED"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-800"
                              }`}
                            >
                               {getApplicationStatus(application.applicationStatus) || "N/A"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-2">
                              {/* Status Update Dropdown */}
                              <select
                                value={tempStatus[application.id] || application.applicationStatus || ""}
                                onChange={(e) => handleStatusChange(application.id, e.target.value)}
                                disabled={updatingStatus[application.id]}
                                className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-hec-500 focus:border-hec-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                              >
                                {statusOptions.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              
                              {/* Update Status Button - Only show if status has changed */}
                              {tempStatus[application.id] && tempStatus[application.id] !== application.applicationStatus && (
                                <button
                                  className="bg-hec-500 hover:bg-hec-600 text-white px-3 py-1.5 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                  onClick={() => handleStatusUpdate(application.id)}
                                  disabled={updatingStatus[application.id]}
                                >
                                  {updatingStatus[application.id] ? 'Updating...' : 'Update Status'}
                                </button>
                              )}
                              
                              {/* Documents Button */}
                              <button
                                className="bg-hec-700 hover:bg-hec-600 text-white px-3 py-1.5 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-hec-500 focus:ring-offset-2 text-xs font-medium"
                                onClick={() => handleOpenDocuments(application)}
                              >
                                Documents
                              </button>
                              
                              {/* Assess Button */}
                              <button
                                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-xs font-medium"
                                onClick={() => navigate(`/assessment/${application.id}/${id}`)}
                              >
                                Assess
                              </button>

                              {/* Audit History Button */}
                              <button
                                onClick={() => setAuditApplication(application)}
                                className="p-1.5 text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                title="View audit info"
                              >
                                <History className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {currentPrograms.length === 0 && !loading && !error && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-hec-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Search className="w-8 h-8 text-hec-400" />
                      </div>
                      <h3 className="text-lg font-medium text-hec-900 mb-2">
                        No applications found
                      </h3>
                      <p className="text-hec-500">
                        Try adjusting your search terms or clear the search to
                        see all applications.
                      </p>
                    </div>
                  )}

                  {/* Modal for documents */}
                  <Modal
                    isOpen={isModalOpen}
                    onRequestClose={handleCloseModal}
                    contentLabel="Student Documents"
                    ariaHideApp={false}
                    className="fixed inset-0 flex items-center justify-center z-50"
                    overlayClassName="fixed inset-0 bg-black bg-opacity-40 z-40"
                  >
                    <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 relative">
                      <button
                        onClick={handleCloseModal}
                        className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-xl font-bold"
                        aria-label="Close"
                      >
                        &times;
                      </button>
                      <h2 className="text-xl font-semibold mb-4 text-hec-800">
                        Applicant Documents
                      </h2>
                      {docLoading ? (
                        <div className="text-center text-hec-600 py-8">
                          Loading documents...
                        </div>
                      ) : docError ? (
                        <div className="text-center text-red-600 py-8">
                          {docError}
                        </div>
                      ) : modalDocuments.length === 0 ? (
                        <div className="text-center text-hec-500 py-8">
                          No documents found for this student.
                        </div>
                      ) : (
                        <ul className="divide-y divide-hec-100">
                          {modalDocuments.map((doc, idx) => {
                            const fileName = doc.documentPath
                              ? doc.documentPath.split("/").pop()
                              : `Document ${idx + 1}`;

                            return (
                              <li
                                key={doc.id || idx}
                                className="py-3 flex items-center justify-between"
                              >
                                <span className="text-hec-900 flex-1">
                                  {fileName}
                                </span>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handlePreviewDocument(doc.id)}
                                    disabled={docActionLoading === `preview-${doc.id}`}
                                    className="px-3 py-1 bg-hec-700 text-white rounded hover:bg-hec-600 text-xs font-medium transition-colors disabled:opacity-60"
                                  >
                                    {docActionLoading === `preview-${doc.id}` ? "Opening..." : "Preview"}
                                  </button>
                                  <button
                                    onClick={() => handleDownloadDocument(doc.id, fileName)}
                                    disabled={docActionLoading === `download-${doc.id}`}
                                    className="px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-500 text-xs font-medium transition-colors disabled:opacity-60"
                                  >
                                    {docActionLoading === `download-${doc.id}` ? "Downloading..." : "Download"}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </Modal>
                </div>
              </div>
            )}
            {/* Pagination Controls */}
            {!loading &&
              !error &&
              filteredPrograms.length > programsPerPage && (
                <div className="flex justify-center mt-8 space-x-2">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {renderPaginationButtons()}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
          </div>
        </div>
        <div>
          <FooterComponents />
        </div>
      </div>

      {/* Bulk Update Modal */}
      <Modal
        isOpen={isBulkUpdateModalOpen}
        onRequestClose={handleCloseBulkUpdateModal}
        contentLabel="Bulk Status Update"
        ariaHideApp={false}
        className="fixed inset-0 flex items-center justify-center z-50"
        overlayClassName="fixed inset-0 bg-black bg-opacity-40 z-40"
      >
        <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full p-6 relative">
          <button
            onClick={handleCloseBulkUpdateModal}
            disabled={isBulkUpdating}
            className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            &times;
          </button>
          
          <h2 className="text-2xl font-bold mb-4 text-hec-800 flex items-center">
            <Send className="w-6 h-6 mr-2" />
            Bulk Status Update
          </h2>
          
          <div className="space-y-4">
            {/* Filter by current status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Current Status (Optional)
              </label>
              <select
                value={bulkFilterStatus}
                onChange={(e) => setBulkFilterStatus(e.target.value)}
                disabled={isBulkUpdating}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-hec-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="ALL">All Applications (except GRANTED)</option>
                <option value="RECEIVED">RECEIVED BY HEC</option>
                <option value="UNDER_REVIEW">UNDER REVIEW</option>
                <option value="SUBMITTED_TO_PARTENER">RECEIVED BY PARTNER</option>
                <option value="SUBMITTED_TO_UNIVERSITY">RECEIVED BY UNIVERSITY</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </div>

            {/* New status to set */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Status <span className="text-red-500">*</span>
              </label>
              <select
                value={bulkUpdateStatus}
                onChange={(e) => setBulkUpdateStatus(e.target.value)}
                disabled={isBulkUpdating}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hec-500 focus:border-hec-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select New Status --</option>
                <option value="RECEIVED">RECEIVED BY HEC</option>
                <option value="UNDER_REVIEW">UNDER REVIEW</option>
                <option value="SUBMITTED_TO_PARTENER">RECEIVED BY PARTNER</option>
                <option value="SUBMITTED_TO_UNIVERSITY">RECEIVED BY UNIVERSITY</option>
                <option value="GRANTED">GRANTED</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </div>

            {/* Preview affected applications */}
            {bulkUpdateStatus && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Preview:</p>
                    <p>
                      This will update <span className="font-bold">{getAffectedApplicationsCount()}</span> application(s)
                      {bulkFilterStatus !== "ALL" && (
                        <span> with current status "{getApplicationStatus(bulkFilterStatus)}"</span>
                      )}
                      {" "}to "<span className="font-bold">{getApplicationStatus(bulkUpdateStatus)}</span>".
                    </p>
                    <p className="mt-2 text-xs text-blue-700">
                      Note: Applications with GRANTED status will be automatically skipped.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Email notification option */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="bulkSendEmail"
                checked={bulkSendEmail}
                onChange={(e) => setBulkSendEmail(e.target.checked)}
                disabled={isBulkUpdating}
                className="w-4 h-4 text-hec-600 border-gray-300 rounded focus:ring-hec-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label htmlFor="bulkSendEmail" className="ml-2 text-sm text-gray-700">
                Send email notifications to applicants
              </label>
            </div>

            {/* Confirmation checkbox */}
            <div className="flex items-center bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <input
                type="checkbox"
                id="bulkConfirm"
                checked={bulkConfirmed}
                onChange={(e) => setBulkConfirmed(e.target.checked)}
                disabled={isBulkUpdating}
                className="w-4 h-4 text-hec-600 border-gray-300 rounded focus:ring-hec-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label htmlFor="bulkConfirm" className="ml-2 text-sm font-medium text-gray-800">
                I confirm this bulk update operation
              </label>
            </div>

            {/* Progress bar */}
            {isBulkUpdating && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between text-sm text-gray-700 mb-2">
                  <span>Updating applications...</span>
                  <span>{bulkUpdateProgress.current} / {bulkUpdateProgress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-hec-600 h-2.5 rounded-full transition-all duration-300"
                    style={{
                      width: `${(bulkUpdateProgress.current / bulkUpdateProgress.total) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            )}

            {/* Results summary */}
            {!isBulkUpdating && (bulkUpdateResults.success > 0 || bulkUpdateResults.failed > 0) && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-2">Update Results:</h3>
                <div className="space-y-1 text-sm">
                  <p className="text-green-600">✓ Success: {bulkUpdateResults.success}</p>
                  {bulkUpdateResults.failed > 0 && (
                    <p className="text-red-600">✗ Failed: {bulkUpdateResults.failed}</p>
                  )}
                  {bulkUpdateResults.skipped > 0 && (
                    <p className="text-yellow-600">⊘ Skipped (GRANTED): {bulkUpdateResults.skipped}</p>
                  )}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={handleCloseBulkUpdateModal}
                disabled={isBulkUpdating}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isBulkUpdating ? 'Cancel' : 'Close'}
              </button>
              <button
                onClick={handleBulkStatusUpdate}
                disabled={!bulkUpdateStatus || !bulkConfirmed || isBulkUpdating || getAffectedApplicationsCount() === 0}
                className="px-4 py-2 bg-hec-600 hover:bg-hec-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Send className="w-4 h-4" />
                <span>{isBulkUpdating ? 'Updating...' : 'Update Status'}</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Audit Modal */}
      {auditApplication && (
        <AbroadAuditModal
          application={auditApplication}
          onClose={() => setAuditApplication(null)}
        />
      )}
    </div>
  );
}

function AbroadAuditModal({ application, onClose }) {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setAudit(null);
    axios
      .get(process.env.REACT_APP_BASE_URL + `hec/abroad-scholarship-application/audit/${application.id}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      .then((res) => { if (!cancelled) { setAudit(res.data); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError("Failed to load audit information."); setLoading(false); } });
    return () => { cancelled = true; };
  }, [application.id]);

  const fmt = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  const statusColors = {
    RECEIVED: "bg-blue-100 text-blue-800",
    UNDER_REVIEW: "bg-yellow-100 text-yellow-800",
    SUBMITTED_TO_PARTENER: "bg-indigo-100 text-indigo-800",
    SUBMITTED_TO_UNIVERSITY: "bg-purple-100 text-purple-800",
    GRANTED: "bg-green-100 text-green-800",
    REJECTED: "bg-red-100 text-red-800",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-400 hover:text-gray-700 text-2xl font-bold leading-none"
        >
          &times;
        </button>

        <div className="flex items-center gap-2 mb-5">
          <History className="w-5 h-5 text-violet-600" />
          <h2 className="text-lg font-semibold text-gray-800">Application Audit</h2>
        </div>

        {loading && (
          <div className="text-center py-8 text-gray-500">Loading audit information...</div>
        )}
        {error && (
          <div className="text-center py-8 text-red-500">{error}</div>
        )}
        {audit && (
          <div className="space-y-4">
            {/* Status */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Current Status</span>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[audit.applicationStatus] || "bg-gray-100 text-gray-700"}`}>
                {audit.applicationStatus || "N/A"}
              </span>
            </div>

            {/* Submitted */}
            <div className="flex items-start gap-2 text-sm">
              <Calendar className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-gray-500 text-xs">Submitted on</p>
                <p className="text-gray-800 font-medium">{fmt(audit.submittedAt)}</p>
              </div>
            </div>

            {/* Last Updated */}
            <div className="flex items-start gap-2 text-sm">
              <Calendar className="w-4 h-4 text-violet-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-gray-500 text-xs">Last updated</p>
                <p className="text-gray-800 font-medium">{fmt(audit.lastUpdatedAt)}</p>
              </div>
            </div>

            {/* Processed by */}
            <div className="border-t pt-4 mt-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Processed by</p>
              {audit.processedByName ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-violet-400 shrink-0" />
                    <span className="text-gray-800 font-medium">{audit.processedByName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <ShieldCheck className="w-4 h-4 text-violet-400 shrink-0" />
                    <span className="text-gray-600">{audit.processedByRole || "N/A"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="w-4 h-4 text-violet-400 shrink-0" />
                    <span className="text-gray-600">{audit.processedByEmail || "N/A"}</span>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">Not yet processed</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}